/**
 * TrendMarkButton — a self-contained mark affordance for trend deltas on
 * the OrgTrends page.
 *
 * Why not the standard MarkButton? Marks are deployment-scoped (the route
 * is /api/deployments/:dep_id/marked-items), and the OrgTrends page shows
 * delta rows whose destination wave varies row-by-row. Rather than
 * mounting a separate <MarkedItemsProvider> per row, this button talks to
 * the API for the specific to_deployment_id passed in. It maintains its
 * own marked-state and looks identical to the standard MarkButton.
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

export default function TrendMarkButton({
  toDeploymentId,
  fromDeploymentId,
  dimension,
  initialMark,
  onChange,
  testIdSuffix,
}) {
  const [mark, setMark] = useState(initialMark || null);
  // OrgTrends fetches /trends and the per-deployment /marked-items sequentially,
  // so this component first mounts with initialMark=undefined and only later
  // receives the saved mark via prop. Re-sync internal state when the prop
  // identity (or `consultant_note`) changes. The async IIFE defers the
  // setState past the effect's synchronous body to satisfy the rule.
  useEffect(() => {
    (async () => { setMark(initialMark || null); })();
  }, [initialMark?.id, initialMark?.consultant_note]);
  const [showPopover, setShowPopover] = useState(false);
  const [note, setNote] = useState(initialMark?.consultant_note || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const existing = mark;

  function openPopover() {
    setNote(existing?.consultant_note || "");
    setError("");
    setShowPopover(true);
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      let saved;
      if (existing) {
        const { data } = await api.patch(
          `/api/deployments/${toDeploymentId}/marked-items/${existing.id}`,
          { consultant_note: note.trim() },
        );
        saved = data;
      } else {
        const { data } = await api.post(
          `/api/deployments/${toDeploymentId}/marked-items`,
          {
            kind: "trend_delta",
            payload: {
              dimension,
              from_deployment_id: fromDeploymentId,
            },
            consultant_note: note.trim(),
          },
        );
        saved = data;
      }
      setMark(saved);
      if (onChange) onChange(saved);
      setShowPopover(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove() {
    if (!existing) return;
    setSaving(true);
    setError("");
    try {
      await api.delete(`/api/deployments/${toDeploymentId}/marked-items/${existing.id}`);
      setMark(null);
      if (onChange) onChange(null);
      setShowPopover(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  }

  const isExisting = Boolean(existing);
  const testId = `mark-btn${testIdSuffix ? `-${testIdSuffix}` : ""}`;

  return (
    <div className="relative inline-block">
      <button
        onClick={openPopover}
        data-testid={testId}
        data-marked={isExisting ? "true" : "false"}
        title={isExisting ? "Edit note · remove" : "Mark this delta for the report"}
        style={{
          fontFamily: "Oswald",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontSize: "10px",
          padding: "5px 10px",
          background: isExisting ? "#1A1A1A" : "transparent",
          color: isExisting ? "#FBFAF7" : "#1A1A1A",
          border: `1px solid ${isExisting ? "#1A1A1A" : "#DDDDDD"}`,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {isExisting ? "✓ Marked" : "+ Mark"}
      </button>
      {showPopover && (
        <div
          className="absolute z-50 mt-2"
          style={{
            right: 0,
            width: "320px",
            background: "#FBFAF7",
            border: "1px solid #DDDDDD",
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            padding: "14px",
          }}
          data-testid={`${testId}-popover`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="eyebrow mb-2" style={{ fontSize: "10px" }}>
            {isExisting ? "Edit note" : "Add to report"}
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="A sentence in your own words about why this matters."
            rows={3}
            maxLength={500}
            className="field w-full text-sm"
            style={{ fontFamily: "Inter", lineHeight: 1.5 }}
            data-testid={`${testId}-note`}
          />
          <div className="text-xs text-gray-500 mt-1" style={{ fontFamily: "Oswald", letterSpacing: "0.08em" }}>
            {note.length} / 500
          </div>
          {error && (
            <div className="text-xs mt-2" style={{ color: "#AD393E" }}>{error}</div>
          )}
          <div className="flex items-center justify-between gap-2 mt-3">
            <button
              onClick={() => setShowPopover(false)}
              className="text-xs"
              style={{
                fontFamily: "Oswald", letterSpacing: "0.12em", textTransform: "uppercase",
                background: "transparent", border: 0, color: "#555", cursor: "pointer", padding: "6px 0",
              }}
              data-testid={`${testId}-cancel`}
            >
              Cancel
            </button>
            <div className="flex items-center gap-2">
              {isExisting && (
                <button
                  onClick={handleRemove}
                  disabled={saving}
                  className="text-xs"
                  style={{
                    fontFamily: "Oswald", letterSpacing: "0.12em", textTransform: "uppercase",
                    background: "transparent", border: "1px solid #AD393E", color: "#AD393E",
                    padding: "6px 10px", cursor: saving ? "wait" : "pointer",
                  }}
                  data-testid={`${testId}-remove`}
                >
                  Remove
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="text-xs"
                style={{
                  fontFamily: "Oswald", letterSpacing: "0.12em", textTransform: "uppercase",
                  background: "#1A1A1A", color: "#FBFAF7", border: 0,
                  padding: "6px 12px", cursor: saving ? "wait" : "pointer",
                }}
                data-testid={`${testId}-save`}
              >
                {isExisting ? "Save" : "Mark"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
